<template>
	<b-table
		class="OrdersTable"
		checkable
		:is-row-checkable="isRowCheckable"
		:checked-rows.sync="checkedRows"
		hoverable
		paginated
		backend-pagination
		:data="orders"
		:columns="columns"
		:loading="isLoading"
		:empty="!isLoading && total === 0"
		:total="total"
		:per-page="perPage"
		backend-sorting
		default-sort-direction="desc"
		:default-sort="[sortParams.field, sortParams.order]"
		@page-change="changePage"
		@sort="changeSortParams"
		@update:checkedRows="$emit('update:checkedRows', $event)"
	>
		<template slot-scope="props">
			<b-table-column field="id" :label="getLabel('id')">{{ props.row.id }}</b-table-column>

			<b-table-column field="clientName" :label="getLabel('clientName')">
				<a href @click.prevent="$emit('order-click', props.row)">
					<span
						v-if="isMontageDateInfoVisible(props.row.montageDate)"
						:title="getMontageDateInfo(props.row.montageDate)"
						class="warning-icon"
						><i class="mdi">warning</i></span
					>
					<span>{{ props.row.clientName }}</span>
				</a>
			</b-table-column>

			<b-table-column field="address" :label="getLabel('address')">
				<span v-if="props.row.lat && props.row.lon" class="address-marker">
					<i class="mdi">location_on</i>
				</span>
				<span v-html="getTableOrderAddress(props.row)"></span>
			</b-table-column>

			<b-table-column
				field="phone"
				:label="getLabel('phone')"
				class="nowrap"
				:visible="currentTab === 'new' && currentTab === 'pending'"
			>
				{{ props.row.phone }}
				<div v-if="props.row.scndPhone">{{ props.row.scndPhone }}</div>
			</b-table-column>

			<b-table-column field="price" :label="getLabel('price')" class="nowrap"
				>{{ props.row.price }} {{ getCurrencyLabel(props.row.currency) }}
				{{ props.row.paymentVat }}</b-table-column
			>

			<b-table-column
				field="route"
				:label="getLabel('route')"
				class="nowrap"
				:visible="currentTab !== 'new' && currentTab !== 'pending'"
			>
				<a
					href
					@click.prevent="$emit('route-click', props.row.route)"
					class="route-link"
					v-if="props.row.route"
				>
					<span
						:style="`background-color: ${getColorByName(props.row.route.color).hex};`"
						class="route-color-preview"
						v-if="props.row.route.color"
					></span>
					<span class="route-name">{{ props.row.route.name }}</span>
				</a>
				<span class="route-name" v-else>{{ props.row.route ? props.row.route.name : '-' }}</span>
			</b-table-column>

			<b-table-column field="montageDate" :label="getLabel('montageDate')">
				<span v-if="props.row.montageDate">{{ props.row.montageDate }}</span>
				<span v-else>-</span>
			</b-table-column>

			<b-table-column
				field="createdDate"
				:label="getLabel('createdDate')"
				:visible="canSortByCreatedDate"
			>
				<div v-if="props.row.createdDate">
					{{ props.row.createdDate | humanDate }}
					<small>{{ props.row.createdDate | date('DD/MM/YYYY') }}</small>
				</div>
				<span v-else>-</span>
			</b-table-column>
		</template>

		<template slot-scope="props" slot="header">
			{{ props.column.label }}
			<i
				v-show="sortParams.field === props.column.field"
				:class="{ mdi: true, 'is-desc': sortParams.order === 'desc' }"
				>arrow_upward</i
			>
		</template>
		<template slot="empty">
			<section class="section">
				<div class="content has-text-grey has-text-centered">
					<p>
						<b-icon icon="emoticon-sad" size="is-large"></b-icon>
					</p>
					<p>Brak zamówień.</p>
				</div>
			</section>
		</template>
		<!-- <template slot="bottom-left" v-if="orders.length">
      <b>Wybranych</b>: {{ checkedRows.length }}
		</template>-->
		<template slot="bottom-left">
			<div class="per-page">
				<span>Pokaż rekordów:</span>
				<b-select @input="changePerPage($event)" :value="perPage">
					<option
						v-for="(value, index) in perPageOptions"
						:key="index"
						:value="value"
						:selected="value === perPage"
					>
						{{ value }}
					</option>
				</b-select>
			</div>
		</template>
	</b-table>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

import config from '@/config'
import bus from '@/EventBus'
import { getColorByName } from '@/helpers'
import OrderService from '@/services/OrderService'

export default {
	data() {
		return {
			orders: [],
			checkedRows: [],
			perPage: localStorage.getItem('orders-table-per-page') || 10,
			perPageOptions: config.company.perPageOptions,
			pageNr: 1,
			sortParams: {
				field: 'id',
				order: 'desc'
			},
			total: 0,
			isLoading: false,
			filter: {}
		}
	},
	computed: {
		...mapState('orders', ['currentTab', 'searchText', 'viewMode', 'filterCountry']),
		columns() {
			return [
				{
					field: 'id',
					label: 'NR',
					sortable: true
				},
				{
					field: 'clientName',
					label: 'Nazwa Klienta',
					sortable: true
				},
				{
					field: 'address',
					label: 'Adres'
				},
				{
					field: 'phone',
					label: 'Telefon',
					visible: this.currentTab === 'new' && this.currentTab === 'pending'
				},
				{
					field: 'price',
					label: 'Cena',
					sortable: true
					// visible: this.currentTab === 'new' && this.currentTab === 'pending'
				},
				{
					field: 'route',
					label: 'Trasa',
					visible: this.currentTab !== 'new' && this.currentTab !== 'pending'
				},
				{
					field: 'montageDate',
					label: 'Data montażu',
					sortable: true
					// visible: this.currentTab !== 'new' && this.currentTab !== 'pending'
				},
				{
					field: 'createdDate',
					label: 'Dodano',
					sortable: true,
					visible: this.canSortByCreatedDate
				}
			]
		},
		canSortByCreatedDate() {
			return config.company.canSortByCreatedDate
		}
	},
	methods: {
		getColorByName(hex) {
			return getColorByName(hex)
		},
		changePage(pageNr) {
			this.pageNr = pageNr
			this.loadOrders()
		},
		changePerPage(num) {
			this.perPage = num
			localStorage.setItem('orders-table-per-page', this.perPage)
			this.loadOrders()
		},
		changeSortParams(field, order) {
			this.sortParams = { field, order }
			this.loadOrders()
		},
		getTableOrderAddress(order) {
			let main = ''
			let aside = ''

			main += order.postalCode ? order.postalCode : ''
			if (order.city) main += ' ' + order.city
			else if (order.post) main += ' ' + order.post

			aside += order.street ? '(' + order.street + ')' : ''

			return main + '<br><small>' + aside + '</small>'
		},
		isMontageDateInfoVisible(montageDate) {
			const montageDateMoment = moment(montageDate, 'DD-MM-YYYY')
			const nowMoment = moment(new Date())
			const diffInDays = montageDateMoment.diff(nowMoment, 'days')

			return diffInDays < 14 && montageDateMoment.isAfter(nowMoment)
		},
		getMontageDateInfo(montageDate) {
			const montageDateMoment = moment(montageDate, 'DD-MM-YYYY')
			const nowMoment = moment(new Date())
			const days = montageDateMoment.diff(nowMoment, 'days')
			const hours = montageDateMoment.diff(nowMoment, 'hours')

			if (days === 0 && hours < 24) return `Montaż za ${hours} godzin!`

			return `Montaż za ${days} dni!`
		},
		isRowCheckable(order) {
			return order && order.lat && order.lon
		},
		loadOrders() {
			this.isLoading = true

			const whereParams = []
			// if (this.filter.by === 'status' && this.filter.value === 'pending')
			// 	whereParams.push({
			// 		whereField: 'montageDate',
			// 		whereCondition: 'after',
			// 		whereValue: moment()
			// 			.add(14, 'days')
			// 			.format('YYYY-MM-DD')
			// 	})

			// if (this.filter.by === 'status' && this.filter.value === 'new') {
			// 	whereParams.push({
			// 		whereField: 'montageDate',
			// 		whereCondition: 'strictly_before',
			// 		whereValue: moment()
			// 			.add(14, 'days')
			// 			.format('YYYY-MM-DD')
			// 	})
			// }

			if (this.filterCountry) {
				whereParams.push({
					whereField: 'country',
					whereCondition: 'equal',
					whereValue: this.filterCountry
				})
			}

			const filter = this.filter && {
				by: this.filter.by,
				// value:
				// 	this.filter.by === 'status' && this.filter.value === 'pending' ? 'new' : this.filter.value
				value: this.filter.value
			}

			const params = {
				sorting: this.sortParams,
				perPage: this.perPage,
				pageNr: this.pageNr,
				filter,
				searchText: this.searchText,
				where: whereParams
			}

			OrderService.getAll(params)
				.then((res) => this.loadOrdersSuccess(res.data))
				.catch((err) => this.loadOrdersFailure(err))
		},
		loadOrdersSuccess(data) {
			this.orders = []
			this.total = data['hydra:totalItems']
			if (this.total / this.perPage > 1000) {
				this.total = this.perPage * 1000
			}
			if (this.filter && this.filter.by === 'status' && this.filter.value === 'new') {
				this.totalNew = this.total
				bus.$emit('new-orders-loaded', this.totalNew)
			}
			this.isLoading = false
			if (this.total === 0) return false
			data['hydra:member'].forEach((item) => {
				this.orders.push(item)
			})
			this.checkedRows = []
			bus.$emit('orders-loaded')
		},
		loadOrdersFailure(err) {
			this.orders = []
			if (this.filter && this.filter.by === 'status' && this.filter.value === 'new')
				this.totalNew = 0
			this.isLoading = false
			throw { error: err }
		},
		getLabel(columnId) {
			return this.columns.filter(({ field }) => field === columnId)[0].label
		},
		getCurrencyLabel(currency) {
			switch (currency) {
				case 'PLN':
					return 'zł'
				case 'CZK':
					return 'Kč'
				default:
					return currency
			}
		}
	},
	filters: {
		date(value, format) {
			return moment(value).format(format)
		},
		humanDate(value) {
			return moment(value).fromNow()
		}
	},
	created() {
		moment.locale('pl')
		bus.$on('tab-change', () => {
			this.filter = null
			if (this.currentTab !== 'all') {
				this.filter = { by: 'status', value: this.currentTab }
			}
			if (this.currentTab === 'pending') {
				this.filter = { by: 'status', value: ['pending', 'complaint'] }
			}
			if (
				(this.currentTab === 'new' || this.currentTab === 'pending') &&
				this.viewMode === 'routes'
			) {
				this.$store.dispatch('orders/selectViewMode', 'orders')
			}

			// deselect country filter if tab is not "pending" or "new"
			if (this.currentTab !== 'pending' && this.currentTab !== 'new') {
				this.$store.dispatch('orders/selectFilterCountry', null)
			}

			this.loadOrders()
		})

		this.events = [
			'order-created',
			'order-updated',
			'order-archived',
			'order-unarchived',
			'order-accepted',
			'order-trashed',
			'order-complaint',
			'order-duplicated',
			'order-deleted',
			'route-attached',
			'route-detached',
			'route-updated',
			'route-archived',
			'route-deleted',
			'search',
			'change-filter-country'
		]
		bus.$on(this.events, () => {
			this.loadOrders()
		})
		bus.$on(['change-view-mode', 'orders-loaded'], () => {
			this.checkedRows = []
		})
	},
	destroyed() {
		bus.$off('tab-change')
		bus.$off(this.events)
	}
}
</script>

<style lang="scss" scoped>
.is-small .mdi {
	font-size: 20px;
}
.address-marker .mdi {
	font-size: 20px;
	vertical-align: middle;
}
td.nowrap {
	white-space: nowrap;
}
[data-label='Dodano'] {
	small {
		display: block;
		font-size: 11px;
		color: #aaa;
	}
}
.route-link {
	& > * {
		vertical-align: middle;
	}
}
.route-color-preview {
	width: 20px;
	height: 20px;
	display: inline-block;
	margin: 5px 5px 0;
	border: 1px solid #ccc;
	border-radius: 3px;
}
.per-page {
	> span {
		margin-right: 10px;
		vertical-align: middle;
		opacity: 0.5;
	}
	> div {
		display: inline-block;
		vertical-align: middle;
	}
}
.warning-icon {
	vertical-align: middle;
	padding-right: 5px;
	animation: blink-animation 1s steps(5, start) infinite;
}
@keyframes blink-animation {
	to {
		color: red;
	}
}
</style>
